/**
 * Deviner a quoi correspond chaque colonne d'un fichier importe.
 *
 * Toute cette logique vivait en tete d'AddProspectDialog, 1964 lignes, sans
 * un test : la distance de Levenshtein, la detection du type d'une colonne
 * a partir de ses valeurs, la reconnaissance d'un prenom et d'un nom
 * separes, et la correspondance automatique. Rien de cela n'a besoin d'un
 * ecran pour etre verifie.
 */

import * as XLSX from 'xlsx';

/** Les champs de prospect vers lesquels une colonne peut pointer. */
export type ChampCible = string;

/** D'ou vient l'association : le nom exact, un nom approchant, ou les valeurs. */
export type NiveauConfiance = 'exact' | 'fuzzy' | 'value';

/** Ce que la detection propose : quelle colonne pour quel champ, et pourquoi. */
export interface Correspondance {
  mapping: Record<ChampCible, string>;
  confidence: Record<ChampCible, NiveauConfiance>;
  /** Renseigne quand le fichier a un prenom et un nom dans deux colonnes. */
  nameSplit?: { firstHeader: string; lastHeader: string } | null;
}

/** Un fichier lu : ses en-tetes et ses lignes, toutes en texte. */
export interface FichierLu {
  headers: string[];
  rows: string[][];
}

/* ── Target fields the user can map to. `labelKey` is resolved at render
   time with t(), so the mapping table follows the active language. ── */
export const TARGET_FIELDS = [
    { key: 'prospect_name', labelKey: 'common:field.name', required: true },
    { key: 'prospect_phone', labelKey: 'common:field.phone', required: true },
    // Second phone target: feeds `additional_phones` (the array ProspectQueue
    // already exposes), NOT a `mobile_phone` field. Without this target a
    // "Portable" column had nowhere to go but a custom field — imported,
    // visible, but never callable.
    { key: 'prospect_mobile', labelKey: 'dialer:addProspect.form.mobile' },
    { key: 'prospect_email', labelKey: 'common:field.email' },
    { key: 'prospect_company', labelKey: 'common:field.company' },
    { key: 'prospect_title', labelKey: 'common:field.title' },
    { key: 'linkedin_url', labelKey: 'dialer:addProspect.form.linkedin' },
    { key: 'website', labelKey: 'dialer:addProspect.form.website' },
    { key: 'notes', labelKey: 'common:field.notes' },
];

/* ── Auto-detect mapping from common column names ── */
export const FIELD_ALIASES: Record<ChampCible, string[]> = {
    prospect_name: ['nom', 'name', 'prospect_name', 'full_name', 'fullname', 'contact', 'prénom', 'prenom', 'first_name', 'last_name', 'nom complet', 'nom et prénom', 'firstname', 'lastname', 'nom & prénom', 'identite'],
    // Mobile-specific terms live ONLY on prospect_mobile below (never here):
    // a header can match at most one target, so keeping them on both would
    // make the winner depend on column order instead of always resolving
    // to "the mobile is number 2" (Kevin, chantier 2).
    prospect_phone: ['telephone', 'téléphone', 'phone', 'tel', 'prospect_phone', 'numéro', 'numero', 'phone_number', 'tél', 'tel.', 'numero de telephone'],
    // Second phone number: feeds `additional_phones` (see TARGET_FIELDS),
    // never the primary `prospect_phone`.
    prospect_mobile: ['portable', 'mobile', 'gsm', 'cell', 'cellphone', 'prospect_mobile', 'tel mobile', 'tel portable', 'numero portable', 'numero mobile', 'telephone portable', 'telephone mobile', 'mobile_phone', 'numero 2', 'telephone 2', 'tel 2', 'phone 2', 'phone2'],
    prospect_email: ['email', 'e-mail', 'mail', 'prospect_email', 'courriel', 'adresse email', 'adresse e-mail', 'e_mail', 'email_pro', 'email professionnel', 'email perso', 'email address'],
    prospect_company: ['entreprise', 'company', 'société', 'societe', 'prospect_company', 'org', 'organisation', 'organization', 'raison sociale', 'société / entreprise', 'employer', 'firme', 'boite'],
    prospect_title: ['poste', 'title', 'fonction', 'job', 'prospect_title', 'role', 'job_title', 'titre', 'intitulé de poste', 'intitule', 'position', 'jobtitle', 'profession'],
    linkedin_url: ['linkedin', 'linkedin_url', 'lien_linkedin', 'profil_linkedin', 'linkedin_profile', 'url linkedin', 'profil linkedin', 'linkedin profile', 'linkedinurl'],
    website: ['website', 'site', 'site_web', 'url', 'web', 'siteweb', 'site web', 'site internet', 'website_url', 'company_website'],
    notes: ['notes', 'note', 'commentaire', 'comment', 'description', 'remarques', 'remarque', 'commentaires', 'observations', 'comments'],
};

/* Normalize header for matching: lowercase, strip diacritics, collapse spaces/underscores/dashes */
export function normalizeHeader(h: unknown): string {
    return String(h)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[\s_\-./]+/g, ' ')
        .trim();
}

/* Levenshtein distance for fuzzy matching (handles typos in headers) */
export function levenshtein(a: string, b: string): number {
    if (a === b) return 0;
    if (!a.length || !b.length) return Math.max(a.length, b.length);
    // Deux lignes suffisent : la precedente et celle en cours. Le tableau
    // complet obligeait a affirmer a chaque acces que la ligne existe.
    let precedente = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        const courante = [i];
        for (let j = 1; j <= b.length; j++) {
            const cout = a[i - 1] === b[j - 1] ? 0 : 1;
            courante[j] = Math.min(
                (precedente[j] ?? 0) + 1,
                (courante[j - 1] ?? 0) + 1,
                (precedente[j - 1] ?? 0) + cout,
            );
        }
        precedente = courante;
    }
    return precedente[b.length] ?? 0;
}

/* Value-level type detectors (confirm header guesses) */
export const TYPE_DETECTORS: Record<ChampCible, (v: string) => boolean> = {
    prospect_email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    prospect_phone: (v) => /^[+]?\d[\d\s().-]{6,}$/.test(v.replace(/\s/g, '')),
    linkedin_url: (v) => /linkedin\.com/i.test(v),
    website: (v) => /^https?:\/\//i.test(v) && !/linkedin\.com/i.test(v),
};

/* Generic detectors for custom fields — detect *types* (date, number) so
 * imported values land typed and can be sorted/filtered later. Returns
 * a hint string (not a TARGET_FIELDS key) used only to label custom keys. */
export const VALUE_TYPE_HINTS: Record<string, (v: string) => boolean> = {
    date: (v) => /^\d{2,4}[-/.]\d{1,2}[-/.]\d{1,4}$/.test(v.trim()),
    number: (v) => /^[+-]?[\d\s,.]+$/.test(v.trim()) && /\d/.test(v),
};

export function detectColumnType(samples: string[]): ChampCible | null {
    if (!samples.length) return null;
    const nonEmpty = samples.filter((s) => s && s.length > 1);
    if (nonEmpty.length === 0) return null;
    // Need at least 60% of non-empty samples to match
    const threshold = Math.max(1, Math.ceil(nonEmpty.length * 0.6));
    for (const [type, detector] of Object.entries(TYPE_DETECTORS)) {
        const matches = nonEmpty.filter((s) => detector(s)).length;
        if (matches >= threshold) return type;
    }
    return null;
}

/* Lightweight type hint for a custom column (not a TARGET_FIELD).
 * Returns 'date', 'number', or 'text'. Used to display a small badge
 * in the UI and to format the value at render time. */
export function detectValueTypeHint(samples: string[]): string | null {
    const nonEmpty = samples.filter((s) => s && s.length > 0);
    if (nonEmpty.length === 0) return 'text';
    const threshold = Math.max(1, Math.ceil(nonEmpty.length * 0.7));
    for (const [hint, detector] of Object.entries(VALUE_TYPE_HINTS)) {
        const matches = nonEmpty.filter((s) => detector(s)).length;
        if (matches >= threshold) return hint;
    }
    return 'text';
}

/* Detect first_name / last_name pair so we can offer to merge into
 * prospect_name. Returns { firstHeader, lastHeader } or null. */
export const FIRST_NAME_ALIASES = ['prenom', 'prénom', 'first name', 'firstname', 'first_name', 'given name'];
export const LAST_NAME_ALIASES = ['nom', 'nom de famille', 'last name', 'lastname', 'last_name', 'family name', 'surname'];

export function detectNameSplit(headers: string[]): { firstHeader: string; lastHeader: string } | null {
    const normalized = headers.map(normalizeHeader);
    const firstAliases = FIRST_NAME_ALIASES.map(normalizeHeader);
    const lastAliases = LAST_NAME_ALIASES.map(normalizeHeader);
    const firstIdx = normalized.findIndex((h) => firstAliases.includes(h));
    const lastIdx = normalized.findIndex((h) => lastAliases.includes(h));
    const firstHeader = headers[firstIdx];
    const lastHeader = headers[lastIdx];
    if (!firstHeader || !lastHeader) return null;
    return { firstHeader, lastHeader };
}

/**
 * Smart auto-detect mapping: combines header alias matching (exact +
 * fuzzy Levenshtein ≤2) with value-type detection (regex on first rows).
 * Value detection wins over header-only matches to correct mislabeled
 * columns ("contact" column that actually contains emails).
 *
 * Returns:
 *   - mapping: { targetField: sourceHeader }
 *   - confidence: { targetField: 'exact' | 'fuzzy' | 'value' }
 *   - nameSplit: { firstHeader, lastHeader } | null — surfaced as a
 *     UI suggestion to concatenate first + last name columns
 */
export function autoDetectMapping(headers: string[], rows: string[][]): Correspondance {
    const mapping: Record<ChampCible, string> = {};
    const confidence: Record<ChampCible, NiveauConfiance> = {};
    const normalized = headers.map(normalizeHeader);

    // 1. Exact alias match — highest confidence
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
        const normalizedAliases = aliases.map(normalizeHeader);
        const idx = normalized.findIndex((h) => normalizedAliases.includes(h));
        const colonne = idx === -1 ? undefined : headers[idx];
        if (colonne && !Object.values(mapping).includes(colonne)) {
            mapping[field] = colonne;
            confidence[field] = 'exact';
        }
    }

    // 2. Fuzzy alias match (Levenshtein ≤2) for remaining target fields
    const assigned = new Set(Object.values(mapping));
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
        if (mapping[field]) continue;
        const normalizedAliases = aliases.map(normalizeHeader);
        let best = { idx: -1, dist: Infinity };
        normalized.forEach((h, idx) => {
            const colonne = headers[idx];
            if (!colonne || assigned.has(colonne)) return;
            for (const alias of normalizedAliases) {
                const d = levenshtein(h, alias);
                if (d < best.dist && d <= 2) best = { idx, dist: d };
            }
        });
        const meilleure = best.idx === -1 ? undefined : headers[best.idx];
        if (meilleure) {
            mapping[field] = meilleure;
            confidence[field] = 'fuzzy';
            assigned.add(meilleure);
        }
    }

    // 3. Value-type detection — fill remaining slots from actual data
    const sampleCount = Math.min(10, rows?.length ?? 0);
    headers.forEach((h, idx) => {
        if (assigned.has(h)) return;
        const samples = (rows ?? []).slice(0, sampleCount).map((r) => String(r[idx] ?? '').trim());
        const detected = detectColumnType(samples);
        if (detected && !mapping[detected]) {
            mapping[detected] = h;
            confidence[detected] = 'value';
            assigned.add(h);
        }
    });

    // A mobile-flavoured header (Portable, GSM, ...) is number 2 only when
    // there IS a number 1. With no landline-flavoured header at all, it is
    // the prospect's only number: promote it to the required prospect_phone
    // slot rather than leaving that field unmapped and the mobile one
    // pointing at a number nobody dials.
    const colonneMobile = mapping.prospect_mobile;
    if (!mapping.prospect_phone && colonneMobile) {
        mapping.prospect_phone = colonneMobile;
        confidence.prospect_phone = confidence.prospect_mobile ?? 'value';
        delete mapping.prospect_mobile;
        delete confidence.prospect_mobile;
    }

    return {
        mapping,
        confidence,
        nameSplit: detectNameSplit(headers),
    };
}

/* ── Re-split a 1-column dataset if every row contains an inline separator ──
   Cas frequent : un utilisateur colle un CSV brut dans Excel, ce qui produit
   une feuille avec 1 seule colonne dont chaque cellule contient
   "Jean,Dupont,06 12 34 56 78". Sans ce helper, le mapping voit 1 seule
   colonne et l'agent doit ouvrir le fichier dans Excel pour le splitter
   manuellement. Avec : on detecte le separateur (',', ';', '\t') si ET seulement si
   - il n'y a qu'un seul header
   - la cellule du header OU plus de 80% des lignes le contiennent
   - le nombre de tokens est >= 2 et consistant sur la majorite des lignes
   Le quoting CSV n'est pas gere ici (cas marginal, Excel l'enleve deja). */
export function redetectInlineSeparator(headers: string[], rows: string[][]): FichierLu {
    if (headers.length !== 1) return { headers, rows };
    const candidates = [',', ';', '\t'];
    const sample = [headers[0], ...rows.slice(0, 20).map(r => r[0])].filter(Boolean);
    if (sample.length < 2) return { headers, rows };
    let best = null;
    for (const sep of candidates) {
        const counts = sample.map(s => (String(s).split(sep).length - 1));
        const withSep = counts.filter(c => c > 0).length;
        if (withSep / sample.length < 0.8) continue;
        // Mode (most frequent non-zero token count) — used as the canonical column count.
        const tokenCounts = sample.map(s => String(s).split(sep).length);
        const freq: Record<number, number> = {};
        for (const t of tokenCounts) freq[t] = (freq[t] ?? 0) + 1;
        const dominant = Number(Object.keys(freq).reduce((a, b) => (freq[Number(a)] ?? 0) >= (freq[Number(b)] ?? 0) ? a : b));
        if (dominant < 2) continue;
        const score = ((freq[dominant] ?? 0) / sample.length) * dominant;
        if (!best || score > best.score) best = { sep, dominant, score };
    }
    if (!best) return { headers, rows };
    const retenu = best;
    const splitRow = (s: unknown) => String(s ?? '').split(retenu.sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
    // Re-split header : si la 1ere ligne ressemble a un header (lettres uniquement,
    // sans chiffres dans la majorite des tokens) on l'utilise. Sinon on genere
    // des noms generiques "Colonne 1..N" et on traite la 1ere ligne comme une row.
    const headerTokens = splitRow(headers[0]);
    const looksLikeHeader = headerTokens.every(t => t && !/^[+\d\s().\-,]+$/.test(t) && t.length < 60);
    let newHeaders;
    let newRows;
    if (looksLikeHeader && headerTokens.length === best.dominant) {
        newHeaders = headerTokens;
        newRows = rows.map(r => splitRow(r[0]));
    } else {
        newHeaders = Array.from({ length: best.dominant }, (_, i) => `Colonne ${i + 1}`);
        newRows = [splitRow(headers[0]), ...rows.map(r => splitRow(r[0]))];
    }
    // Normaliser : chaque row au meme nombre de colonnes que newHeaders.
    newRows = newRows.map(r => {
        if (r.length === newHeaders.length) return r;
        if (r.length < newHeaders.length) return [...r, ...Array(newHeaders.length - r.length).fill('')];
        return r.slice(0, newHeaders.length);
    });
    return { headers: newHeaders, rows: newRows };
}

/* ── Parse any file (CSV or Excel) into { headers, rows } ── */
export async function parseFile(file: File): Promise<FichierLu> {
    const ext = (file.name.split('.').pop() ?? '').toLowerCase();

    if (ext === 'csv' || ext === 'tsv' || ext === 'txt') {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) return { headers: [], rows: [] };
        const firstLine = lines[0] ?? '';
        const sep = firstLine.includes(';') ? ';' : firstLine.includes('\t') ? '\t' : ',';
        const parseRow = (line: string) => line.split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
        const headers = parseRow(firstLine);
        const rows = lines.slice(1).map(parseRow);
        return redetectInlineSeparator(headers, rows);
    }

    // Excel: read via SheetJS
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet) return { headers: [], rows: [] };
    // `header: 1` rend un tableau de lignes, chaque ligne un tableau de
    // cellules : SheetJS ne le sait pas dans ses types, d'ou l'annotation.
    const jsonData = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
    if (jsonData.length < 2) return { headers: [], rows: [] };
    const headers = (jsonData[0] ?? []).map((h) => String(h).trim());
    const rows = jsonData.slice(1).map((r) => r.map((c) => String(c ?? '').trim()));
    return redetectInlineSeparator(headers, rows);
}
